/**
 * usePipelineCrud — manages open/edit/delete pipeline flows and their dialog state.
 *
 * Extracted from usePipelineManager to isolate pipeline lifecycle actions
 * (open/load, edit metadata, delete) from save-dialog orchestration.
 */

import React from 'react';

import type { ISemanticType } from '@/interfaces/ISemanticType';
import type { ISavedPipeline } from '@/interfaces/IStorage';
import { useAppStore } from '@/store/useAppStore';
import { useEditorStore } from '@/store/useEditorStore';
import { usePipelineStore } from '@/store/usePipelineStore';

export function usePipelineCrud(
  setLoadedOutputSemanticType: React.Dispatch<React.SetStateAction<string | null>>,
  semanticTypeByName: (name: string | null | undefined) => ISemanticType | null,
  resetStepSelection: () => void,
  clearToolSelection: () => void,
) {
  /* ---------- store selectors ---------- */
  const savedPipelines = usePipelineStore((s) => s.savedPipelines);
  const updatePipeline = usePipelineStore((s) => s.updatePipeline);
  const deletePipeline = usePipelineStore((s) => s.deletePipeline);

  const { loadPipeline } = useAppStore();
  const activePipelineId = useEditorStore((s) => s.activePipelineId);
  const setActivePipelineName = useEditorStore((s) => s.setActivePipelineName);
  const setActivePipelineDescription = useEditorStore((s) => s.setActivePipelineDescription);
  const setActivePipelineId = useEditorStore((s) => s.setActivePipelineId);
  const setInputType = useEditorStore((s) => s.setInputType);
  const setInputTypeMode = useEditorStore((s) => s.setInputTypeMode);

  /* ---------- local dialog state ---------- */
  const [editOpen, setEditOpen] = React.useState(false);
  const [editId, setEditId] = React.useState<string | null>(null);
  const [editName, setEditName] = React.useState('');
  const [editDescription, setEditDescription] = React.useState('');
  const [editError, setEditError] = React.useState<string | null>(null);

  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [deleteId, setDeleteId] = React.useState<string | null>(null);

  /* ---------- pipeline actions ---------- */

  const openPipeline = React.useCallback(
    (pipeline: ISavedPipeline) => {
      loadPipeline(pipeline);

      setLoadedOutputSemanticType(pipeline.outputSemanticType ?? null);
      const loadedInputType = semanticTypeByName(pipeline.inputSemanticType);
      if (loadedInputType) {
        setInputTypeMode('manual');
        setInputType(loadedInputType);
      }

      resetStepSelection();
      clearToolSelection();
    },
    [loadPipeline, semanticTypeByName, setInputType, setInputTypeMode, resetStepSelection, clearToolSelection]
  );

  const openEditPipeline = React.useCallback((pipeline: ISavedPipeline) => {
    setEditId(pipeline.id);
    setEditName(pipeline.name);
    setEditDescription(pipeline.description ?? '');
    setEditError(null);
    setEditOpen(true);
  }, []);

  const openDeletePipeline = React.useCallback((pipeline: ISavedPipeline) => {
    setDeleteId(pipeline.id);
    setDeleteOpen(true);
  }, []);

  /* ---------- dialog-level handlers ---------- */

  const handleEditSave = React.useCallback(() => {
    if (!editId) return;
    const name = editName.trim();
    if (!name) {
      setEditError('Name is required.');
      return;
    }

    const description = editDescription.trim() || undefined;
    updatePipeline(editId, { name, description });

    if (activePipelineId === editId) {
      setActivePipelineName(name);
      setActivePipelineDescription(description ?? null);
    }

    setEditOpen(false);
    setEditError(null);
    setEditId(null);
  }, [
    activePipelineId,
    editDescription,
    editId,
    editName,
    setActivePipelineDescription,
    setActivePipelineName,
    updatePipeline,
  ]);

  const handleDelete = React.useCallback(() => {
    if (!deleteId) return;
    deletePipeline(deleteId);

    if (activePipelineId === deleteId) {
      setActivePipelineId(null);
    }

    setDeleteOpen(false);
    setDeleteId(null);
  }, [activePipelineId, deleteId, deletePipeline, setActivePipelineId]);

  /* ---------- assembled dialog prop objects ---------- */

  const editDialogProps = React.useMemo(
    () => ({
      open: editOpen,
      name: editName,
      description: editDescription,
      error: editError,
      onChangeName: (v: string) => {
        setEditName(v);
        setEditError(null);
      },
      onChangeDescription: setEditDescription,
      onClose: () => {
        setEditOpen(false);
        setEditError(null);
        setEditId(null);
      },
      onSave: handleEditSave,
    }),
    [editOpen, editName, editDescription, editError, handleEditSave]
  );

  const deleteDialogProps = React.useMemo(
    () => ({
      open: deleteOpen,
      pipeline: deleteId ? savedPipelines.find((p) => p.id === deleteId) ?? null : null,
      onCancel: () => {
        setDeleteOpen(false);
        setDeleteId(null);
      },
      onDelete: handleDelete,
    }),
    [deleteOpen, deleteId, savedPipelines, handleDelete]
  );

  return {
    openPipeline,
    openEditPipeline,
    openDeletePipeline,
    editDialogProps,
    deleteDialogProps,
  };
}
